import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import size from 'lodash/size';
import get from 'lodash/get';
import cn from 'classnames';

import getToDoAssigmentText from '../../../../utils/getToDoAssigmentText';
import { isDesktop } from '../../../../utils/mobileHelpers';
import { ERROR_CODES } from '../../../../constants/errors';
import { timeToShow } from '../../utils/handleTaskTime';
import SingleSelect from '../../../common/SingleSelect';
import ErrorBlock from '../../../common/ErrorBlock';
import { TODO_TYPES } from '../../../../constants';
import InfoCardPopup from '../../../InfoCardPopup';
import GoogleMaps from '../../../GoogleMaps';
import { TaskDocuments } from './TaskDocuments';
import Loader from '../../../common/Loader';
import Popup from '../../../common/Popup';
import { isCompleted } from '../../utils';
import Button from '../../../Button';
import { enhance } from './enhance';
import styles from './styles.styl';

const TASK_ATTACHMENT_LIMIT = 5;
const { TASK_CUSTOM } = TODO_TYPES;

function TaskDetailsPopup({
  task,
  clear,
  error,
  taskId,
  custom,
  userId,
  onClose,
  succeed,
  pending,
  complete,
  clearCustom,
  completeCustom,
  intl: { formatMessage },
}) {
  const [errorText, setError] = useState(null);
  const [outcomeId, setOutcomeId] = useState(null);
  const [attachments, setAttachment] = useState([]);
  const [isOpenLocations, toggleOpenLocations] = useState(false);

  const {
    error: errorCustom,
    pending: pendingCustom,
    succeed: succeedCustom,
  } = custom;

  useEffect(() => {
    if (succeed || succeedCustom) handleClose();
  }, [succeed, succeedCustom]);

  useEffect(() => {
    if (error || errorCustom) {
      const isAlreadyCompleted =
        get(error, 'code') === ERROR_CODES.TASK_ALREADY_COMPLETED;
      const textId = isAlreadyCompleted
        ? 'task.alreadyCompleted'
        : 'errors.common';
      setError(formatMessage({ id: textId }));
    }
  }, [error, errorCustom]);

  const {
    todoType,
    todoTask,
    expiredTime,
    todoStatus,
    taskAssignmentId,
    completedTime,
    taskOutcomes,
    chosenOutcome,
    outcomeAttachments,
  } = task || {};
  const { name, description, links, geolocationsPresent, dueDescription } =
    todoTask || {};

  const completed = useMemo(() => isCompleted(todoStatus), [todoStatus]);
  const hasOutcomesList = useMemo(() => !isEmpty(taskOutcomes), [taskOutcomes]);

  const outcomesList = useMemo(
    () =>
      hasOutcomesList &&
      taskOutcomes.map(item => {
        return {
          value: item.id,
          label: item.text,
        };
      }),
    [taskOutcomes],
  );

  const popupTitle = useMemo(() => {
    const textId = completed ? 'task.completed' : 'task.upcoming';
    return formatMessage({ id: textId });
  }, [completed]);

  const getOutcomeValue = (selectedId, param) => {
    const outcome =
      hasOutcomesList && taskOutcomes.find(({ id }) => id === selectedId);
    return get(outcome, param);
  };

  const attachmentRequired = getOutcomeValue(outcomeId, 'attachmentEnabled');

  const disabled = useMemo(() => {
    const noRequiredAttachment = !size(attachments) && attachmentRequired;

    return (
      (hasOutcomesList && !outcomeId) ||
      pending ||
      pendingCustom ||
      size(attachments) > TASK_ATTACHMENT_LIMIT ||
      noRequiredAttachment
    );
  }, [
    attachments,
    attachmentRequired,
    hasOutcomesList,
    outcomeId,
    attachments,
    pending,
    pendingCustom,
  ]);

  const handleOptionChange = useCallback(value => {
    setOutcomeId(value);
  }, []);

  const showDocuments = completed
    ? !isEmpty(outcomeAttachments)
    : attachmentRequired;

  const completedOutcomeText = getOutcomeValue(chosenOutcome, 'text');

  const handleComplete = () => {
    const data = {
      status: 'completed',
      id: taskAssignmentId,
      userId,
      outcomeId,
      attachments,
      attachmentRequired,
    };

    todoType === TASK_CUSTOM ? completeCustom(data) : complete(data);
  };

  const handleClose = () => {
    setError(null);
    onClose();
    clear();
    clearCustom();
  };

  return (
    <InfoCardPopup
      title={popupTitle}
      onClose={handleClose}
      icon="assigned-task"
      headerInfo={timeToShow(
        completedTime,
        expiredTime,
        dueDescription,
        formatMessage,
      )}
      headerTitle={name}
      headerSubtitle={getToDoAssigmentText(task, formatMessage)}
      isOpened={Boolean(taskId)}
      width={isDesktop() ? 'small' : 'medium'}
    >
      <div className={styles.content}>
        <If condition={pending || pendingCustom}>
          <Loader className="absolute-center" />
        </If>
        <p className="mb2">{description}</p>
        <If condition={!isEmpty(links)}>
          {links.map(link => (
            <a
              href={get(link, 'urlLink')}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
              key={get(link, 'id')}
            >
              {get(link, 'urlDescription')}
            </a>
          ))}
        </If>
        <If condition={geolocationsPresent}>
          <Button
            className={cn('mt3', styles.actionButton)}
            border
            color="accent"
            onClick={() => toggleOpenLocations(true)}
          >
            {formatMessage({ id: 'task.seeLocations' })}
          </Button>
        </If>
        <If condition={hasOutcomesList && !completed}>
          <div className={styles.section} key="outcome">
            <h3 className={styles.title}>
              {formatMessage({ id: 'task.selectOutcome' })}
            </h3>
            <SingleSelect
              name="outcome"
              list={outcomesList}
              selectClass={styles.list}
              radioInputClass="mr2"
              onChange={handleOptionChange}
              selectedValue={outcomeId}
            />
          </div>
        </If>
        <If condition={Boolean(completedOutcomeText) && completed}>
          <div className={styles.section} key="outcomeText">
            <h3 className={styles.title}>
              {formatMessage({ id: 'task.outcome' })}
            </h3>
            <p>{completedOutcomeText}</p>
          </div>
        </If>
        <If condition={showDocuments}>
          <div className={styles.section} key="doc">
            <TaskDocuments
              disabled={size(attachments) >= TASK_ATTACHMENT_LIMIT}
              formatMessage={formatMessage}
              completed={completed}
              attachments={completed ? outcomeAttachments : attachments}
              addAttachment={setAttachment}
            />
          </div>
        </If>
        <If condition={errorText}>
          <ErrorBlock className="mt2 mb2" errors={errorText} />
        </If>
        <If condition={!completed}>
          <Button
            className={cn('mt4', styles.actionButton)}
            color="primary"
            onClick={handleComplete}
            disabled={disabled}
          >
            {formatMessage({ id: 'common.complete' })}
          </Button>
        </If>
      </div>
      <Popup
        strictClose
        opened={isOpenLocations}
        onClose={() => toggleOpenLocations(false)}
        title={formatMessage({ id: 'googleMaps.popup.title' })}
        mobileFullScreen={false}
        overflowVisible
        withoutAdaptive
      >
        <div className={styles.width}>
          <GoogleMaps />
        </div>
      </Popup>
    </InfoCardPopup>
  );
}

TaskDetailsPopup.propTypes = {
  error: PropTypes.object,
  succeed: PropTypes.bool,
  pending: PropTypes.bool,
  clear: PropTypes.func,
  task: PropTypes.object,
  taskId: PropTypes.number,
  onClose: PropTypes.func,
  complete: PropTypes.func,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
  userId: PropTypes.number,
  custom: PropTypes.object,
  clearCustom: PropTypes.func,
  completeCustom: PropTypes.func,
};

export default enhance(TaskDetailsPopup);



// WEBPACK FOOTER //
// ./src/components/Healthboard/Components/TaskDetailsPopup/index.jsx